import { useState, useEffect, Suspense } from "react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { StripeError } from '@stripe/stripe-js';
import { SubscriptionCard } from "@/components/settings/SubscriptionCard";
import { useStripeInitializer } from "@/components/settings/stripe/StripeInitializer";
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle 
} from "@/components/ui/alert-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

export const SubscriptionManager = () => {
  const priceIds = {
    monthly: "price_1QvOaiG3RRUf49cQgmkP8MtT",
    yearly: "price_1QvObYG3RRUf49cQp6XPRkNN",
    lifetime: "price_1QvOcGG3RRUf49cQlU0O22em"
  };

  const priceIdToPlanMap = {
    "price_1QvOaiG3RRUf49cQgmkP8MtT": "monthly",
    "price_1QvObYG3RRUf49cQp6XPRkNN": "yearly",
    "price_1QvOcGG3RRUf49cQlU0O22em": "lifetime"
  };
  
  const planTypeToPriceIdMap = {
    "monthly": "price_1QvOaiG3RRUf49cQgmkP8MtT",
    "yearly": "price_1QvObYG3RRUf49cQp6XPRkNN",
    "lifetime": "price_1QvOcGG3RRUf49cQlU0O22em"
  };

  const [currentPlan, setCurrentPlan] = useState<string | null>(null);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [verificationAttempts, setVerificationAttempts] = useState(0);
  const [currentSubscriptionId, setCurrentSubscriptionId] = useState<string | null>(null);
  const [currentCustomerId, setCurrentCustomerId] = useState<string | null>(null);
  const [trialEndsAt, setTrialEndsAt] = useState<string | null>(null);
  const [accessEndsAt, setAccessEndsAt] = useState<string | null>(null);
  const { toast } = useToast();
  const navigate = useNavigate();
  const stripePromise = useStripeInitializer();

  const loadSubscriptionStatus = async (isRetry = false) => {
    try {
      if (!isRetry) {
        setLoading(true);
      }
      
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        navigate('/login');
        return;
      }

      console.log('Checking subscription status for user:', session.user.id);

      const { data: subscriptions, error } = await supabase
        .from('subscriptions')
        .select('plan_type, status, created_at, stripe_subscription_id, stripe_customer_id, trial_ends_at, current_period_end')
        .eq('user_id', session.user.id)
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) {
        console.error('Error fetching subscription:', error);
        throw error;
      }

      console.log('Latest subscription data from database:', subscriptions);

      const activeSubscription = subscriptions?.find(sub => 
        sub.status === 'active' || 
        sub.status === 'trialing' || 
        (sub.status === 'trialing' && sub.trial_ends_at && new Date(sub.trial_ends_at) > new Date())
      );
      
      if (activeSubscription) {
        console.log('Setting current plan from database to:', activeSubscription.plan_type);
        console.log('Subscription status:', activeSubscription.status);
        console.log('Trial ends at:', activeSubscription.trial_ends_at);
        console.log('Current period ends at:', activeSubscription.current_period_end);
        
        setCurrentPlan(activeSubscription.status === 'trialing' ? 'trialing' : activeSubscription.plan_type);
        setCurrentSubscriptionId(activeSubscription.stripe_subscription_id);
        setCurrentCustomerId(activeSubscription.stripe_customer_id);
        setTrialEndsAt(activeSubscription.trial_ends_at);
        setAccessEndsAt(null);
        setLoading(false);
        return;
      }

      const canceledSubscription = subscriptions?.find(sub => 
        sub.status === 'canceled' && 
        sub.current_period_end && 
        new Date(sub.current_period_end) > new Date()
      );

      if (canceledSubscription) {
        console.log('Found canceled subscription with remaining access until:', canceledSubscription.current_period_end);
        setCurrentPlan('canceled');
        setCurrentSubscriptionId(canceledSubscription.stripe_subscription_id);
        setCurrentCustomerId(canceledSubscription.stripe_customer_id);
        setAccessEndsAt(canceledSubscription.current_period_end);
        setLoading(false);
        return;
      }

      console.log('No active subscription found in database, checking with Stripe...');
      
      try {
        console.log('Calling verify-stripe-subscription function');
        const { data: stripeCheckResult, error: stripeCheckError } = await supabase.functions.invoke('verify-stripe-subscription', {
          body: { userId: session.user.id }
        });
        
        if (stripeCheckError) {
          console.error('Error checking Stripe subscription:', stripeCheckError);
          throw stripeCheckError;
        }
        
        console.log('Stripe verification result:', stripeCheckResult);
        
        if (stripeCheckResult && (stripeCheckResult.hasActiveSubscription || stripeCheckResult.message === "Recent payment found")) {
          const planType = stripeCheckResult.subscription?.metadata?.plan_type || 'monthly';
          console.log('Active subscription found in Stripe with plan type:', planType);
          
          try {
            const subscriptionId = stripeCheckResult.subscription?.id || 'unknown';
            const customerId = stripeCheckResult.subscription?.customer || 'unknown';
            
            console.log('Upserting subscription with data:', {
              user_id: session.user.id,
              plan_type: planType,
              status: 'active',
              stripe_subscription_id: subscriptionId,
              stripe_customer_id: customerId
            });
            
            const { error: insertError, data: insertResult } = await supabase
              .from('subscriptions')
              .insert({
                user_id: session.user.id,
                plan_type: planType,
                status: 'active',
                stripe_subscription_id: subscriptionId,
                stripe_customer_id: customerId,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString()
              });

            if (insertError) {
              console.error('Error inserting subscription in database:', insertError);
              
              const { error: updateError } = await supabase
                .from('subscriptions')
                .update({
                  plan_type: planType,
                  status: 'active',
                  stripe_subscription_id: subscriptionId,
                  stripe_customer_id: customerId,
                  updated_at: new Date().toISOString()
                })
                .eq('user_id', session.user.id);
                
              if (updateError) {
                console.error('Error updating subscription in database:', updateError);
                throw updateError;
              }
            }
          } catch (dbError) {
            console.error('Database operation failed when updating subscription:', dbError);
          }
          
          setCurrentPlan(planType);
          setCurrentSubscriptionId(stripeCheckResult.subscription?.id || null);
          setCurrentCustomerId(stripeCheckResult.subscription?.customer || null);
          setLoading(false);
        } else {
          console.log('No active subscription found after checking with Stripe');
          
          if (verificationAttempts < 3) {
            console.log(`Retry attempt ${verificationAttempts + 1} for subscription verification`);
            setVerificationAttempts(prev => prev + 1);
            
            setTimeout(() => {
              loadSubscriptionStatus(true);
            }, 3000);
            return;
          }
          
          setCurrentPlan(null);
          setCurrentSubscriptionId(null);
          setCurrentCustomerId(null);
          setLoading(false);
        }
      } catch (stripeError) {
        console.error('Error in Stripe verification:', stripeError);
        if (subscriptions && subscriptions.length > 0) {
          const latestSub = subscriptions[0];
          console.log('Using most recent subscription from database:', latestSub);
          if (latestSub.status === 'trialing' && latestSub.trial_ends_at) {
            setCurrentPlan('trialing');
            setTrialEndsAt(latestSub.trial_ends_at);
          } else {
            setCurrentPlan(latestSub.plan_type);
          }
          setCurrentSubscriptionId(latestSub.stripe_subscription_id);
          setCurrentCustomerId(latestSub.stripe_customer_id);
        } else {
          setCurrentPlan(null);
          setCurrentSubscriptionId(null);
          setCurrentCustomerId(null);
        }
        setLoading(false);
      }
    } catch (error) {
      console.error('Error loading subscription status:', error);
      toast({
        title: "Error",
        description: "Failed to load subscription status",
        variant: "destructive",
      });
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSubscriptionStatus();

    const subscriptionChannel = supabase
      .channel('subscription-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'subscriptions'
        },
        (payload) => {
          console.log('Subscription changed:', payload);
          loadSubscriptionStatus();
        }
      )
      .subscribe();

    return () => {
      subscriptionChannel.unsubscribe();
    };
  }, [navigate]);

  const handleSubscribe = async (priceId: string) => {
    const planType = priceIdToPlanMap[priceId];
    
    if (planType === currentPlan) {
      return;
    }
    
    setSelectedPlan(priceId);
    setShowPaymentDialog(true);
  };

  const confirmCancelSubscription = () => {
    setShowCancelDialog(true);
  }

  const handleCancelSubscription = async () => {
    setShowCancelDialog(false);
    
    try {
      setCancelling(true);
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        navigate('/login');
        return;
      }

      const { data: currentSubscriptions, error: fetchError } = await supabase
        .from('subscriptions')
        .select('stripe_subscription_id, plan_type, current_period_end')
        .eq('user_id', session.user.id)
        .eq('status', 'active')
        .order('created_at', { ascending: false })
        .limit(1);

      if (fetchError) {
        console.error('Error fetching current subscription:', fetchError);
        throw new Error('Failed to fetch subscription details');
      }

      if (!currentSubscriptions || currentSubscriptions.length === 0) {
        toast({
          title: "Error",
          description: "No active subscription found to cancel",
          variant: "destructive",
        });
        return;
      }

      const subscription = currentSubscriptions[0];
      let accessEndDate = subscription.current_period_end;
      
      if (!subscription.stripe_subscription_id || subscription.stripe_subscription_id === 'unknown') {
        toast({
          title: "Error",
          description: "Valid subscription ID not found",
          variant: "destructive",
        });
        return;
      }

      try {
        const { data: cancelData, error: cancelError } = await supabase.functions.invoke('create-subscription', {
          body: {
            action: 'cancel',
            subscriptionId: subscription.stripe_subscription_id,
          },
        });

        if (cancelError) {
          console.error('Error cancelling subscription:', cancelError);
          throw new Error('Failed to cancel subscription');
        }
        
        console.log('Cancellation response:', cancelData);
        
        if (cancelData && cancelData.current_period_end) {
          accessEndDate = new Date(cancelData.current_period_end * 1000).toISOString();
        }

        const { error: updateError, data: updateResult } = await supabase
          .from('subscriptions')
          .update({ 
            status: 'canceled', 
            updated_at: new Date().toISOString(),
            current_period_end: accessEndDate
          })
          .eq('stripe_subscription_id', subscription.stripe_subscription_id);

        if (updateError) {
          console.error('Error updating subscription status in database:', updateError);
        } else {
          console.log('Successfully updated subscription status to canceled:', updateResult);
        }

        setAccessEndsAt(accessEndDate);
        setCurrentPlan('canceled');
        await loadSubscriptionStatus();

        toast({
          title: "Subscription Canceled",
          description: "Your subscription has been canceled. You will have access until the end of your billing period.",
        });
      } catch (cancelError) {
        console.error('Error in cancellation process:', cancelError);
        
        const { error: updateError, data: updateResult } = await supabase
          .from('subscriptions')
          .update({ status: 'canceled', updated_at: new Date().toISOString() })
          .eq('stripe_subscription_id', subscription.stripe_subscription_id);
          
        if (!updateError) {
          console.log('Successfully marked subscription as canceled in database:', updateResult);
          toast({
            title: "Subscription Marked as Canceled",
            description: "There was an issue with Stripe, but your subscription has been marked as canceled in our system.",
          });
          await loadSubscriptionStatus();
        } else {
          console.error('Failed to mark subscription as canceled:', updateError);
          throw new Error('Failed to cancel subscription. Please contact support.');
        }
      }
    } catch (error) {
      console.error('Error canceling subscription:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to cancel subscription",
        variant: "destructive",
      });
    } finally {
      setCancelling(false);
    }
  };

  const handlePaymentSuccess = async (paymentMethodId: string) => {
    try {
      setProcessingPayment(true);
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        navigate('/login');
        return;
      }

      let upgradeMessage = '';
      const selectedPlanType = selectedPlan ? priceIdToPlanMap[selectedPlan] : null;

      if (selectedPlanType === 'lifetime' && currentPlan && currentSubscriptionId) {
        console.log(`Upgrading from ${currentPlan} to lifetime plan with existing subscription ID: ${currentSubscriptionId}`);
        upgradeMessage = `You've successfully upgraded from ${currentPlan} to the lifetime plan.`;
        
        const { data, error } = await supabase.functions.invoke('create-subscription', {
          body: {
            action: 'upgrade',
            userId: session.user.id,
            subscriptionId: currentSubscriptionId,
            planType: 'lifetime',
            priceId: priceIds.lifetime,
            paymentMethodId
          },
        });

        if (error) {
          console.error('Error upgrading to lifetime:', error);
          throw error;
        }

        if (!data || !data.success) {
          console.error('Failed to upgrade to lifetime:', data);
          throw new Error('Failed to upgrade to lifetime plan');
        }

        console.log('Lifetime upgrade successful:', data);
        setCurrentPlan('lifetime');
        setShowPaymentDialog(false);
        
        await new Promise(resolve => setTimeout(resolve, 1000));
        await loadSubscriptionStatus();
        
        toast({
          title: "Success",
          description: upgradeMessage,
        });
        
        setProcessingPayment(false);
        return;
      }
      
      if (currentPlan && 
          currentSubscriptionId && 
          currentSubscriptionId !== 'unknown' && 
          currentSubscriptionId !== 'pending_sync' &&
          !currentSubscriptionId.startsWith('lifetime_') && 
          selectedPlanType !== 'lifetime') {
        
        console.log(`Upgrading from ${currentPlan} to ${selectedPlanType} with subscription ID: ${currentSubscriptionId}`);
        upgradeMessage = `You've successfully upgraded from ${currentPlan} to ${selectedPlanType} with prorated billing.`;
        
        const { data, error } = await supabase.functions.invoke('create-subscription', {
          body: {
            action: 'upgrade',
            userId: session.user.id,
            subscriptionId: currentSubscriptionId,
            planType: selectedPlanType,
            priceId: selectedPlan,
          },
        });

        if (error) {
          console.error('Error upgrading subscription:', error);
          throw error;
        }

        if (!data || !data.success) {
          console.error('Failed to upgrade subscription:', data);
          throw new Error('Failed to upgrade subscription');
        }

        console.log('Subscription upgraded successfully:', data);
        setCurrentPlan(selectedPlanType);
        setShowPaymentDialog(false);
        
        await new Promise(resolve => setTimeout(resolve, 1000));
        await loadSubscriptionStatus();
        
        toast({
          title: "Success",
          description: upgradeMessage,
        });
        
        setProcessingPayment(false);
        return;
      }
      
      console.log('Creating new subscription or switching to lifetime plan:', selectedPlanType);
      
      const { data: currentSubscriptions, error: fetchError } = await supabase
        .from('subscriptions')
        .select('stripe_subscription_id, plan_type, stripe_customer_id')
        .eq('user_id', session.user.id)
        .eq('status', 'active');

      if (fetchError) {
        console.error('Error fetching subscriptions:', fetchError);
      } else if (currentSubscriptions && currentSubscriptions.length > 0) {
        for (const subscription of currentSubscriptions) {
          if (subscription.stripe_subscription_id && 
              subscription.stripe_subscription_id !== 'unknown' &&
              !subscription.stripe_subscription_id.startsWith('lifetime_')) {
            
            console.log('Cancelling existing subscription:', subscription.stripe_subscription_id);
            upgradeMessage = `You've successfully switched from ${subscription.plan_type} to ${selectedPlanType}.`;
            
            try {
              const { data: cancelData, error: cancelError } = await supabase.functions.invoke('create-subscription', {
                body: {
                  action: 'cancel',
                  subscriptionId: subscription.stripe_subscription_id,
                },
              });

              if (cancelError) {
                console.error('Error cancelling subscription:', cancelError);
              } else {
                console.log('Cancel subscription response:', cancelData);

                const { error: updateError } = await supabase
                  .from('subscriptions')
                  .update({ status: 'canceled', updated_at: new Date().toISOString() })
                  .eq('stripe_subscription_id', subscription.stripe_subscription_id);

                if (updateError) {
                  console.error('Error updating subscription status in database:', updateError);
                }
              }
            } catch (cancelError) {
              console.error('Error in cancellation process:', cancelError);
            }
          }
        }
      }

      console.log('Creating new subscription:', selectedPlanType);
      const { data, error } = await supabase.functions.invoke('create-subscription', {
        body: {
          userId: session.user.id,
          planType: selectedPlanType,
          priceId: selectedPlan,
          paymentMethodId,
          action: 'create',
        },
      });

      if (error) {
        console.error('Error creating subscription:', error);
        throw error;
      }

      if (!data || !data.success) {
        console.error('Failed to create subscription:', data);
        throw new Error('Failed to create subscription');
      }

      console.log('New subscription created successfully:', data);
      
      try {
        await supabase.functions.invoke('send-notification-email', {
          body: {
            userId: session.user.id,
            type: currentPlan ? 'subscription_updated' : 'subscription_created',
            data: {
              planType: selectedPlanType
            }
          }
        });
      } catch (emailError) {
        console.error('Error sending subscription notification email:', emailError);
      }

      setShowPaymentDialog(false);
      setCurrentPlan(selectedPlanType);
      await loadSubscriptionStatus();

      toast({
        title: "Success",
        description: upgradeMessage || "Your subscription has been updated",
      });

    } catch (error) {
      console.error('Error processing subscription:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to process subscription",
        variant: "destructive",
      });
    } finally {
      setProcessingPayment(false);
    }
  };

  const handlePaymentError = (error: StripeError | Error) => {
    console.error('Payment error:', error);
    toast({
      title: "Error",
      description: error.message || "An unknown error occurred",
      variant: "destructive",
    });
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Subscription</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="bg-gray-100 p-4 rounded mb-6">
            <Skeleton className="h-6 w-2/3 mx-auto" />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="relative">
                <CardHeader className="pb-8 text-center">
                  <Skeleton className="h-6 w-24 mx-auto mb-4" />
                  <Skeleton className="h-8 w-32 mx-auto mt-4" />
                  <Skeleton className="h-4 w-40 mx-auto mt-2" />
                </CardHeader>
                <CardContent>
                  <Skeleton className="h-10 w-full" />
                </CardContent>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <SubscriptionCard
        currentPlan={currentPlan}
        processingPayment={processingPayment || loading}
        showPaymentDialog={showPaymentDialog}
        selectedPlan={selectedPlan}
        stripePromise={stripePromise}
        onSubscribe={handleSubscribe}
        onCancel={confirmCancelSubscription}
        cancelling={cancelling}
        onPaymentSuccess={handlePaymentSuccess}
        onPaymentError={handlePaymentError}
        setShowPaymentDialog={setShowPaymentDialog}
        trialEndsAt={trialEndsAt}
        accessEndsAt={accessEndsAt}
      />
      
      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Subscription</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to cancel your subscription? You'll continue to have access until the end of your current billing period.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Subscription</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleCancelSubscription}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {cancelling ? "Cancelling..." : "Yes, Cancel Subscription"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
